/**
 * PlanPicker — "Choose a starting package"
 *
 * 4 pre-built package cards with budget slider, flight picker, target area.
 * Seller picks a recommendation and adjusts if needed.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import {
  PACKAGE_TEMPLATES, FLIGHT_OPTIONS, TARGET_AREA_OPTIONS, formatDollars,
} from '@/components/deal/mockDealData';

export default function PlanPicker() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();

  const [selectedId, setSelectedId] = useState('pkg-starter');
  const [budgets, setBudgets] = useState(() => {
    const b = {};
    PACKAGE_TEMPLATES.forEach((p) => (b[p.id] = p.budgetRange.default));
    return b;
  });
  const [flights, setFlights] = useState(() => {
    const f = {};
    PACKAGE_TEMPLATES.forEach((p) => (f[p.id] = p.flightDefault));
    return f;
  });
  const [targets, setTargets] = useState(() => {
    const t = {};
    PACKAGE_TEMPLATES.forEach((p) => (t[p.id] = p.targetingDefault));
    return t;
  });
  const [customAddress, setCustomAddress] = useState('');
  const [customizeOpen, setCustomizeOpen] = useState({});

  useEffect(() => {
    deal.setCurrentStage('plan');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    const template = PACKAGE_TEMPLATES.find((p) => p.id === selectedId);
    if (!template) return;

    deal.setSelectedPackage({
      ...template,
      budget: budgets[selectedId],
      flight: flights[selectedId],
      targetArea: targets[selectedId],
      targetAddress: targets[selectedId] === 'custom_address' ? customAddress : null,
    });

    navigate(`/org/${orgSlug}/deal/${dealId}/proposal`);
  };

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="plan"
        onBack={() => navigate(-1)}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="text-center mb-2">
          <h1 className="text-2xl font-bold text-foreground">Choose a starting package</h1>
          <p className="text-muted-foreground mt-1">Pick one. You can adjust later.</p>
        </div>

        {/* Package cards */}
        <div className="grid sm:grid-cols-2 gap-3">
          {PACKAGE_TEMPLATES.map((pkg) => {
            const Icon = pkg.icon;
            const isSelected = selectedId === pkg.id;
            const budget = budgets[pkg.id];
            const flight = flights[pkg.id];
            const target = targets[pkg.id];

            return (
              <Card
                key={pkg.id}
                className={`transition-all cursor-pointer ${
                  isSelected
                    ? 'border-2 border-coral shadow-sm'
                    : 'border border-border hover:border-border/80'
                }`}
                onClick={() => setSelectedId(pkg.id)}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-coral/10' : 'bg-muted'}`}>
                        <Icon className={`h-4 w-4 ${isSelected ? 'text-coral' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{pkg.name}</h3>
                        <p className="text-xs text-muted-foreground">{pkg.tagline}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 bg-coral rounded-full flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    {pkg.recommended && !isSelected && (
                      <Badge className="bg-coral/10 text-coral border-coral/20 text-[10px]">
                        Recommended
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">{pkg.description}</p>

                  {/* Budget */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Budget</span>
                      <span className="text-sm font-semibold text-foreground">{formatDollars(budget)}</span>
                    </div>
                    <Slider
                      value={[((budget - pkg.budgetRange.min) / (pkg.budgetRange.max - pkg.budgetRange.min)) * 100]}
                      onValueChange={([v]) => {
                        const val = Math.round(pkg.budgetRange.min + (v / 100) * (pkg.budgetRange.max - pkg.budgetRange.min));
                        setBudgets((prev) => ({ ...prev, [pkg.id]: val }));
                      }}
                      max={100}
                      step={1}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                      <span>{formatDollars(pkg.budgetRange.min)}</span>
                      <span>{formatDollars(pkg.budgetRange.max)}</span>
                    </div>
                  </div>

                  {/* Flight quick picks */}
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Flight</span>
                    <div className="flex gap-1.5">
                      {FLIGHT_OPTIONS.map((f) => (
                        <button
                          key={f.weeks}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFlights((prev) => ({ ...prev, [pkg.id]: f.weeks }));
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer ${
                            flight === f.weeks
                              ? 'bg-coral text-white'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Customize (collapsible) */}
                  <Collapsible
                    open={customizeOpen[pkg.id]}
                    onOpenChange={(v) => setCustomizeOpen((prev) => ({ ...prev, [pkg.id]: v }))}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                      >
                        <ChevronDown className={`h-3 w-3 transition-transform ${customizeOpen[pkg.id] ? 'rotate-180' : ''}`} />
                        Customize targeting
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2">
                      {TARGET_AREA_OPTIONS.map((t) => (
                        <label
                          key={t.id}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 text-xs cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={`target-${pkg.id}`}
                            checked={target === t.id}
                            onChange={() => setTargets((prev) => ({ ...prev, [pkg.id]: t.id }))}
                            className="accent-coral"
                          />
                          {t.label}
                        </label>
                      ))}
                      {target === 'custom_address' && (
                        <Input
                          value={customAddress}
                          onChange={(e) => setCustomAddress(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Enter address or area"
                          className="text-xs h-8"
                        />
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <Button
          className="w-full h-12 text-base font-semibold rounded-xl shadow-sm bg-coral hover:bg-coral/90 text-white"
          onClick={handleContinue}
        >
          Continue with {PACKAGE_TEMPLATES.find((p) => p.id === selectedId)?.name}
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </>
  );
}
